/*
  Article + Guide table-of-contents.

  Runs on any page whose main section has class .single-article (blog Article
  template, Guide page template, future templates that share the layout).

  Behaviour:
    1. Walks every <h2> inside .single-article-content and assigns a slugified
       id (German umlauts → ae/oe/ue/ss). Existing ids are preserved.
    2. If the sidebar TOC list is empty (no metafield-driven TOC), populates it
       from those H2s — zero editor work per article. Scales to 200+ guides.
    3. If the metafield TOC IS populated, leaves it alone (manual override).
    4. Wires smooth-scroll on click and active-link tracking on scroll.
    5. Hides the entire TOC container if there are no headings to link to.
*/

class Article {
  constructor() {
    const root = document.querySelector('.single-article');
    if (!root) return;

    const header = document.querySelector('#shopify-section-header');
    this.headerOffset = header ? header.clientHeight : 0;

    const tocContainer = root.querySelector('.table-of-contents');
    const tocList = root.querySelector('.table-of-contents-list');
    const content = root.querySelector('.single-article-content');

    if (content) {
      this.assignHeadingIds(content);
      if (tocList && tocContainer && tocContainer.classList.contains('table-of-contents--auto')) {
        this.populateAutoToc(content, tocList, tocContainer);
      }
    }

    this.tocLinks = root.querySelectorAll('.table-of-contents li a');
    this.sections = root.querySelectorAll('.single-article-content h2[id]');
    this.tableOfContentsBtn = root.querySelector('.table-of-contents-button');

    if (this.tocLinks.length === 0 && tocContainer) {
      tocContainer.style.display = 'none';
      return;
    }

    this.tableOfContents();
  }

  slugify(text) {
    return (text || '')
      .toLowerCase()
      .replace(/ä/g, 'ae')
      .replace(/ö/g, 'oe')
      .replace(/ü/g, 'ue')
      .replace(/ß/g, 'ss')
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .substring(0, 60);
  }

  assignHeadingIds(content) {
    const used = new Set();
    document.querySelectorAll('[id]').forEach(el => used.add(el.id));
    content.querySelectorAll('h2').forEach(h2 => {
      if (h2.id) {
        used.add(h2.id);
        return;
      }
      let base = this.slugify(h2.textContent);
      if (!base) return;
      let candidate = base;
      let n = 2;
      while (used.has(candidate)) {
        candidate = `${base}-${n++}`;
      }
      h2.id = candidate;
      used.add(candidate);
    });
  }

  populateAutoToc(content, tocList, tocContainer) {
    const h2s = content.querySelectorAll('h2[id]');
    if (h2s.length === 0) return;

    const items = Array.from(h2s).map((h2, i) => {
      const link = document.createElement('a');
      link.href = `#${h2.id}`;
      link.textContent = h2.textContent.trim();
      if (i === 0) link.classList.add('active');
      const li = document.createElement('li');
      li.appendChild(link);
      return li;
    });

    tocList.innerHTML = '';
    items.forEach(li => tocList.appendChild(li));
    tocContainer.classList.remove('is-empty');
  }

  tableOfContents() {
    if (this.tocLinks.length === 0) return;

    const self = this;
    const headerEl = document.getElementById('shopify-section-header');
    const headerHeight = headerEl ? headerEl.offsetHeight : 0;

    this.tocLinks.forEach(link => {
      link.addEventListener('click', function (event) {
        event.preventDefault();
        const targetId = this.getAttribute('href').replace('#', '');
        const target = document.getElementById(targetId);
        if (target) {
          const top = target.getBoundingClientRect().top + window.scrollY - headerHeight;
          window.scrollTo({ top, behavior: 'smooth' });
        }
        self.tocLinks.forEach(l => l.classList.remove('active'));
        this.classList.add('active');
      });
    });

    this.changeLinkState();
    window.addEventListener('scroll', () => this.changeLinkState());

    if (this.tableOfContentsBtn) {
      this.tableOfContentsBtn.addEventListener('click', () => {
        const toc = document.querySelector('.table-of-contents');
        if (toc) toc.classList.toggle('active');
      });
    }
  }

  changeLinkState = () => {
    if (this.sections.length === 0 || this.tocLinks.length === 0) return;
    let index = this.sections.length;
    while (--index && window.scrollY + this.headerOffset + 20 < this.sections[index].offsetTop) {
      // walk back to the last heading we've scrolled past
    }
    if (index < 0) index = 0;
    this.tocLinks.forEach(link => link.classList.remove('active'));
    if (this.tocLinks[index]) this.tocLinks[index].classList.add('active');
  };
}

new Article();
